import * as React from 'react'

import { Box, Icon, Spinner, Text, Tooltip, useToggle } from '@factorialco/deprecated-design-system'
import moment from 'moment'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import i18n from 'modules/core/lib/i18n'
import users from 'modules/core/store/collections/users'

import contractVersions from 'modules/contracts/store/collections/contractVersions'
import { type Employees } from 'modules/employees/store/collections/employees'
import { employeeManagedEmployees } from 'modules/employees/store/collections/employeesContractVersions'
import GenderStackedBar from 'modules/org_chart/components/GenderStackedBar'
import { OrgChartStateContext } from 'modules/org_chart/components/Provider'
import DetailsValue from 'modules/org_chart/components/SidePanel/DetailsValue'
import EmployeeReporteesAnalyticsCollection from 'modules/org_chart/store/collections/employeeReporteesAnalytics'

interface Props {
  employees: Employees
}

const Overview: React.FC<Props> = ({ employees }) => {
  const { activeEmployee } = React.useContext(OrgChartStateContext)
  const [isTooltipOpen, toggleIsTooltipOpen] = useToggle(false)
  const analytics = EmployeeReporteesAnalyticsCollection.get(activeEmployee?.attributes.id)
  const averageAge = analytics?.attributes.average_age.results[0]
  const averageTenure = analytics?.attributes.tenure.results[0]
  const genderDiversity = analytics?.attributes.diversity.results

  const { loading } = useAsyncData(
    () => EmployeeReporteesAnalyticsCollection.fetchById(activeEmployee?.attributes.id!),
    [activeEmployee?.attributes.id]
  )

  return (
    <Box
      padding="s24"
      gap="s16"
      flexDirection="column"
      alignItems="flexStart"
      border={{
        bottom: {
          color: 'grey400',
          width: 's1',
          style: 'solid',
        },
      }}
    >
      <Box flexDirection="row" alignItems="center" gap="s6">
        <Text.Header2>
          {i18n.t('employees.orgchart.all_reportees')} (
          {activeEmployee
            ? employeeManagedEmployees(activeEmployee, contractVersions, employees).length
            : null}
          )
        </Text.Header2>
        <Tooltip.Alt
          open={isTooltipOpen}
          toggle={toggleIsTooltipOpen}
          attachment="top center"
          label={i18n.t('employees.orgchart.all_reportees_tooltip', {
            name: (() => {
              if (!activeEmployee) return undefined
              const userId = activeEmployee.attributes.access_id
              const user = users.get(userId)
              if (!user) {
                throw Error(`Error getting users with id = ${userId}`)
              }
              return user.firstName
            })(),
          })}
        >
          <Icon.Medium icon="info-circle-line" color="grey800" />
        </Tooltip.Alt>
      </Box>
      {averageAge && (
        <DetailsValue label={i18n.t('employees.common.average_age')}>
          {averageAge?.value} years
        </DetailsValue>
      )}
      {averageTenure && (
        <DetailsValue label={i18n.t('employees.common.average_tenure')}>
          {moment(averageTenure.value).fromNow()}
        </DetailsValue>
      )}
      {loading ? (
        <Box height="s80" alignItems="center" justifyContent="center" width="full">
          <Spinner />
        </Box>
      ) : (
        !!genderDiversity?.length && (
          <DetailsValue label={i18n.t('employees.common.gender_distribution')}>
            <Box paddingTop="s8">
              <GenderStackedBar results={genderDiversity} />
            </Box>
          </DetailsValue>
        )
      )}
    </Box>
  )
}

export default Overview
